import React, { useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CTitle } from '../../components/Widget';
import { useUpdateComponentState } from '../../../hooks';
import MisconceptionCards, {
  MisconceptionCardIconRender,
} from '../../components/MisconceptionCards';
import IdentifyWebsite from '../../components/IdentifyWebsite';
import RealCaseContent from '../../components/RealCaseContent';
import CaseTabsContent from '../../components/CaseTabsContent';
import caseImg from '../../../../../../assets/sme/uats/case_top_bg.png';

interface ISection2Props {
  settings: any;
}

const PackageIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m7.5 4.27 9 5.15" />
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </svg>
);

const MehIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="8" y1="15" x2="16" y2="15" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

const FilterIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const CpuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M15 2v2M9 2v2M15 20v2M9 20v2M20 15h2M20 9h2M2 15h2M2 9h2" />
  </svg>
);

const FREE_RISK_ICONS: MisconceptionCardIconRender[] = [
  () => <PackageIcon />,
  () => <MehIcon />,
  () => <FilterIcon />,
  () => <CpuIcon />,
];

const Section2: React.FunctionComponent<ISection2Props> = ({ settings }) => {
  const ID = '3';
  const param = useParams();
  const p_course = param.course || '';
  const p_topic = param['*'] || 'welcome';
  const paragraphsSettings = useMemo(
    () => settings?.paragraphs[2],
    [settings?.paragraphs]
  );
  const updateComponentState = useUpdateComponentState();

  const agentRiskSteps = useMemo(
    () =>
      (paragraphsSettings?.agentRisks ?? []).map(
        (
          risk: { no?: string; title: string; desc: string },
          index: number
        ) => ({
          id: risk.no ?? index + 1,
          content: `<div style="font-weight: bold;margin-bottom: 0.5rem;word-break: keep-all;">${risk.title}</div>${risk.desc}`,
        })
      ),
    [paragraphsSettings?.agentRisks]
  );
  const handleMisconceptionCardsComplete = React.useCallback(() => {
    updateComponentState(p_course, p_topic, ID, 'sme-uats-section2-1');
  }, [updateComponentState, p_course, p_topic, ID]);

  const handleIdentifyWebsiteComplete = useCallback(() => {
    updateComponentState(p_course, p_topic, ID, 'sme-uats-section2-2');
  }, [updateComponentState, p_course, p_topic]);

  return (
    <div className="topic-section-2">
      <CTitle title={paragraphsSettings?.title} />
      <MisconceptionCards
        title=""
        tips={paragraphsSettings?.freeDesc}
        items={paragraphsSettings?.freeRisks}
        icons={FREE_RISK_ICONS}
        id="sme-uats-section2-1"
        onComplete={handleMisconceptionCardsComplete}
      />

      <div className="identify-website-box">
        <IdentifyWebsite
          title={paragraphsSettings?.agentTitle}
          subTitle={paragraphsSettings?.agentDesc}
          steps={agentRiskSteps}
          onComplete={handleIdentifyWebsiteComplete}
          id="sme-uats-section2-2"
        />
      </div>

      <RealCaseContent
        backgroundImage={caseImg}
        headingLines={paragraphsSettings?.exampleHeading}
        overviewList={paragraphsSettings?.overviewList}
        dataSource={paragraphsSettings?.dataSource}
        dataSourceList={paragraphsSettings.dataSourceList}
      />
      <CaseTabsContent item={paragraphsSettings?.case} />
    </div>
  );
};

export default React.memo(Section2);
